import {Alert, Anchor, Code, Container, Stack, Text} from "@mantine/core";
import {useContext, useState} from "react";
import Version from "../Version.js";
import {IMessageOfTheDay, OmniContext} from "../OmniContext.ts";
import {
    IconAlertHexagon,
    IconCalendarExclamation,
    IconCalendarQuestion,
} from "@tabler/icons-react";
import {TablerWebfontIcon} from "@/components/icon_picker/TablerIconPicker.js";

function MOTDBanner(props: {motd: IMessageOfTheDay | null}) {
    const [dismissed, setDismissed] = useState(false)
    let motd = props.motd
    if (motd && !dismissed) {
        return motd.body && <Alert title={motd.title} color={motd.color} withCloseButton={motd.dismissible !== "" ? motd.dismissible === "true" : false} onClose={() => setDismissed(true)} icon={<TablerWebfontIcon icon={motd.icon} size={"1.25em"}/>}>
            <Text style={{overflowWrap: "anywhere"}} fz={"sm"} dangerouslySetInnerHTML={{__html: motd.body}}/>
        </Alert>
    }
}

function AzureSecretExpireWarning(props: {date: string | null, appId: string, siteName: string}) {
    let today = new Date()
    today.setHours(0)
    today.setMinutes(0)
    today.setSeconds(0)
    today.setMilliseconds(0)
    let todayPlusOneWeek = new Date()
    todayPlusOneWeek.setDate(today.getDate()+7)
    if (props.date && props.date !== "ignore") {
        let expireDate = Date.parse(props.date)
        if (expireDate <= todayPlusOneWeek.getTime()) {
            return <Alert icon={<IconCalendarExclamation/>} title={"Warning: Azure Secret Expiration in less than 1 Week"} color={"red"}>
                <Text>The Microsoft Azure Client Secret generated for {props.siteName} will expire on {props.date}. This will break user authentication if not resolved.</Text>
                <Text>To resolve the issue, log in to the <Anchor href={"https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/Credentials/appId/"+props.appId} target={"_blank"} rel={"noreferrer"}>Azure App Registration Configuration page</Anchor>, select &apos;Certificates &amp; secrets&apos; from the sidebar if not selected, and generate a new Client Secret.</Text>
                <Text>
                    The generated Client Secret and its Expiration Date should be placed in the <Code>AZURE_CLIENT_SECRET</Code> and <Code>AZURE_SECRET_EXPIRATION_DATE</Code> variables in the <Code>backend/omni/app_config.py</Code> file for this deployment.
                    After these variables have been updated, restart the backend application to clear this message.
                </Text>

            </Alert>
        }
    } else if (props.date === "" && props.appId !== "") {
        return <Alert icon={<IconCalendarQuestion/>} title={"Warning: Azure Secret Expiration Unset"} color={"orange"}>
            <Text>The Expiration Date for the Microsoft Azure Client Secret generated for {props.siteName} has not been saved in the config. This means that {props.siteName} cannot notify you of impending secret expiration. When the secret expires, user authentication will break.</Text>
            <Text>To resolve the issue, log in to the <Anchor href={"https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/Credentials/appId/"+props.appId} target={"_blank"} rel={"noreferrer"}>Azure App Registration Configuration page</Anchor>, select &apos;Certificates &amp; secrets&apos; from the sidebar if not selected, and copy the expiration date for the Client Secret in use by this deployment.</Text>
            <Text>The Expiration Date should be placed in the <Code>AZURE_SECRET_EXPIRATION_DATE</Code> variable in the <Code>backend/omni/app_config.py</Code> file for this deployment.</Text>
            <Text>Alternatively, to ignore and clear this message, you can set <Code>AZURE_SECRET_EXPIRATION_DATE = &quot;ignore&quot;</Code></Text>
            <Text>After the variable has been updated, restart the backend application to clear this message.</Text>
        </Alert>
    }
}

function AllPageNotices() {
    const context = useContext(OmniContext)
    const e = context.environment
    return <Container size={"xl"} mt={"sm"}>
        <Stack gap={"xs"}>
            {(e && e.version !== Version) && <Alert icon={<IconAlertHexagon/>} title={"Warning: System Version Mismatch"} color={"red"}>
                API Version: {e.version}<br/>
                Frontend Version: {Version}<br/>
                If API Version &gt; Frontend Version, try refreshing the page to get the updated version.
            </Alert>}
            {(e && context.user && context.user.hasSystemPermissionByName("Dev", "View Debug")) &&
                <AzureSecretExpireWarning date={e.azureSecretExpireDate} appId={e.azureAppID} siteName={e.siteName}/>}
            <MOTDBanner motd={e ? e.motd ?? null : null}/>
        </Stack>
    </Container>
}
export default AllPageNotices